// /web/src/pages/projects/Areas.jsx
import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApi } from "../../lib/api";

import "../../styles/calculator.css";

function makeOrdinal(n) {
  const map = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];
  return map[n - 1] || `${n}th`;
}

function computeLabels(count, entryIdx) {
  const out = new Array(count);
  for (let i = 0; i < count; i++) {
    const delta = i - entryIdx;
    if (delta === 0) out[i] = "Ground floor";
    else if (delta > 0) out[i] = `${makeOrdinal(delta)} floor`;
    else {
      const n = Math.abs(delta);
      out[i] = n === 1 ? "Basement" : `Basement ${n}`;
    }
  }
  return out;
}

function preventNumberScroll(event) {
  event.currentTarget.blur();
}

export default function Areas() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [project, setProject] = React.useState(null);
  const [floors, setFloors] = React.useState([]); // [{i, gea, gia, nia, h}]
  const [entryIndex, setEntryIndex] = React.useState(0); // which row is entry/ground (0-based)
  const [labels, setLabels] = React.useState([]);        // computed only for display

  const inputRefs = React.useRef({
    gea: [],
    gia: [],
    nia: [],
    h: [],
  });

  const [manual, setManual] = React.useState({
    gea: false,
    gia: true,  // GIA manually entered by default
    nia: false,
  });
  const [ratios, setRatios] = React.useState({
    gea_from_gia: 1.053, // default requested
    nia_from_gia: 0.8,
  });

  const floorCount = Number(project?.floors_count ?? floors.length ?? 0);

  React.useEffect(() => {
    const keys = Object.keys(inputRefs.current);
    keys.forEach((key) => {
      inputRefs.current[key] = inputRefs.current[key].slice(0, floors.length);
    });
  }, [floors.length]);

  // ----- helpers -----
  function updateManual(key) {
    setManual((m) => ({ ...m, [key]: !m[key] }));
  }

  function onRatioChange(e) {
    const { name, value } = e.target;
    const num = value === "" ? "" : Number(value);
    setRatios((r) => ({ ...r, [name]: num }));
  }

  function setCell(idx, key, value) {
    setFloors((rows) => {
      const copy = rows.slice();
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  }

  function registerInput(key, idx) {
    return (el) => {
      inputRefs.current[key][idx] = el || null;
    };
  }

  function handleVerticalTab(event, rowIndex, key) {
    if (event.key !== "Tab") return;

    const refs = inputRefs.current[key] || [];
    const direction = event.shiftKey ? -1 : 1;
    let nextIndex = rowIndex + direction;

    while (nextIndex >= 0 && nextIndex < refs.length) {
      const nextEl = refs[nextIndex];
      if (nextEl && !nextEl.disabled) {
        event.preventDefault();
        window.requestAnimationFrame(() => {
          nextEl.focus();
          if (typeof nextEl.select === "function") {
            nextEl.select();
          }
        });
        return;
      }
      nextIndex += direction;
    }
  }

  function computeRow(row) {
    const toNum = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const r_gea = Number(ratios.gea_from_gia || 0);
    const r_nia = Number(ratios.nia_from_gia || 0);

    const geaVal = toNum(row.gea);
    const giaVal = toNum(row.gia);
    const niaVal = toNum(row.nia);

    let inferredGia = giaVal;
    if ((inferredGia === null || inferredGia === undefined) && manual.nia && niaVal !== null && r_nia > 0) {
      const candidate = niaVal / r_nia;
      if (Number.isFinite(candidate)) {
        inferredGia = candidate;
      }
    }
    if ((inferredGia === null || inferredGia === undefined) && manual.gea && geaVal !== null && r_gea > 0) {
      const candidate = geaVal / r_gea;
      if (Number.isFinite(candidate)) {
        inferredGia = candidate;
      }
    }

    const next = { ...row };

    if (!manual.gia) {
      next.gia = inferredGia != null ? inferredGia.toFixed(2) : "";
    }

    const giaForDerived = inferredGia != null ? inferredGia : giaVal;

    if (!manual.gea) {
      const gea = giaForDerived != null && r_gea > 0 ? giaForDerived * r_gea : null;
      next.gea = gea != null ? gea.toFixed(2) : "";
    }
    if (!manual.nia) {
      const nia = giaForDerived != null && r_nia > 0 ? giaForDerived * r_nia : null;
      next.nia = nia != null ? nia.toFixed(2) : "";
    }
    // h (floor-to-floor) is always manual; leave as-is
    return next;
  }

  // ----- load project & set initial rows -----
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api(`/projects/${id}`);
        if (cancelled) return;
        setProject(p);

        let savedRows = [];
        try {
          const res = await api(`/projects/${id}/inputs`);
          if (Array.isArray(res) && res.length > 0) {
            savedRows = res.slice().sort((a, b) => a.floor_index - b.floor_index);
          }
        } catch {
          savedRows = [];
        }
        if (cancelled) return;

        const defaultsManual = { gea: false, gia: true, nia: false };
        const defaultsRatios = { gea_from_gia: 1.053, nia_from_gia: 0.8 };

        const declaredCount = Number(p?.floors_count || 0);
        const maxIndex = savedRows.reduce(
          (acc, row) => Math.max(acc, Number(row?.floor_index ?? -1)),
          -1,
        );
        const fallbackCount = Math.max(maxIndex + 1, 0);
        const count = declaredCount > 0 ? declaredCount : fallbackCount;
        const baseRows = Array.from({ length: count }, (_, i) => ({
          i,
          gea: "",
          gia: "",
          nia: "",
          h: "",
        }));

        if (savedRows.length === 0) {
          if (!cancelled) {
            setFloors(baseRows);
            setEntryIndex(0);
            setManual(defaultsManual);
            setRatios(defaultsRatios);
            setError("");
          }
          return;
        }

        const rowMap = new Map(savedRows.map((row) => [Number(row.floor_index), row]));
        const toStr = (value) => (value === null || value === undefined ? "" : String(value));
        const mergedRows = baseRows.map((row, idx) => {
          const existing = rowMap.get(idx);
          if (!existing) return row;
          return {
            i: idx,
            gea: toStr(existing.gea_m2),
            gia: toStr(existing.gia_m2),
            nia: toStr(existing.nia_m2),
            h: toStr(existing.floor_to_floor_height_m),
          };
        });

        const geaManual = Boolean(savedRows[0]?.gea_locked);
        const giaManual = savedRows[0]?.gia_locked === undefined
          ? true
          : Boolean(savedRows[0]?.gia_locked);
        const niaManual = Boolean(savedRows[0]?.nia_locked);
        const nextManual = { gea: geaManual, gia: giaManual, nia: niaManual };

        const nextRatios = { ...defaultsRatios };
        if (!geaManual) {
          const sample = savedRows.find((r) => {
            const gea = Number(r?.gea_m2);
            const gia = Number(r?.gia_m2);
            return r?.gea_m2 != null && r?.gia_m2 != null && Number.isFinite(gea) && Number.isFinite(gia) && gia !== 0;
          });
          if (sample) {
            const ratio = Number(sample.gea_m2) / Number(sample.gia_m2);
            if (Number.isFinite(ratio) && ratio > 0) {
              nextRatios.gea_from_gia = Number(ratio.toFixed(3));
            }
          }
        }
        if (!niaManual) {
          const sample = savedRows.find((r) => {
            const nia = Number(r?.nia_m2);
            const gia = Number(r?.gia_m2);
            return r?.nia_m2 != null && r?.gia_m2 != null && Number.isFinite(nia) && Number.isFinite(gia) && gia !== 0;
          });
          if (sample) {
            const ratio = Number(sample.nia_m2) / Number(sample.gia_m2);
            if (Number.isFinite(ratio) && ratio > 0) {
              nextRatios.nia_from_gia = Number(ratio.toFixed(3));
            }
          }
        }

        const savedEntry = savedRows.findIndex((r) => r.is_entrance_floor);
        const entry = savedEntry >= 0 && savedEntry < mergedRows.length ? savedEntry : 0;

        if (!cancelled) {
          setFloors(mergedRows);
          setEntryIndex(entry);
          setManual(nextManual);
          setRatios(nextRatios);
          setError("");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load project");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, api]);

  // recompute labels when entry changes or count changes
  React.useEffect(() => {
    setLabels(computeLabels(floors.length, entryIndex));
  }, [entryIndex, floors.length]);

  // recompute computed cells when ratios/manual change
  React.useEffect(() => {
    setFloors((rows) => rows.map(computeRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, ratios]);

  async function onSave() {
    try {
      setError("");

      // 1) Materialize computed cells so we persist what the user sees
      const finalized = floors.map((row) => computeRow(row));
      const toNum = (v) =>
        v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v);

      // 2) Build API payload using finalized values
      const payload = finalized.map((r, idx) => ({
        floor_index: idx,
        is_entrance_floor: idx === entryIndex,
        gea_m2: toNum(r.gea),
        gia_m2: toNum(r.gia),
        nia_m2: toNum(r.nia),
        floor_to_floor_height_m: toNum(r.h),
        gea_locked: Boolean(manual.gea),
        gia_locked: Boolean(manual.gia),
        nia_locked: Boolean(manual.nia),
      }));

      await api(`/projects/${id}/inputs`, { method: "PUT", body: payload });
      navigate(`/projects/${id}/calculators`);
    } catch (e) {
      setError(e?.message || "Failed to save inputs");
    }
  }

  if (loading) {
    return (
      <section className='pd-section pd-calculator'>
        <div className='pd-container narrow'>
          <p className='pd-eyebrow'>Projects</p>
          <h1 className='pd-h2'>Input areas</h1>
          <p className='pd-muted'>Loading project…</p>
        </div>
      </section>
    );
  }

  return (
    <section className='pd-section pd-calculator'>
      <div className='pd-container narrow'>
        <div className='pd-calculator__header'>
          <div>
            <p className='pd-eyebrow'>Projects</p>
            <h1 className='pd-h2'>Input areas</h1>
            <p className='pd-muted'>
              {project?.name ? <strong>{project.name}</strong> : `Project ${id}`} · {floorCount} floor
              {floorCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className='pd-actions'>
            <Link to={`/projects/${id}`} className='pd-btn pd-btn--ghost'>
              Back to project
            </Link>
            <button className='pd-btn pd-btn--primary' onClick={onSave} type='button'>
              Save
            </button>
          </div>
        </div>

        {error && (
          <div className='pd-inline-alert pd-inline-alert--error'>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className='pd-panel'>
          <div className='pd-panel__head'>
            <div>
              <p className='pd-panel__title'>Area settings</p>
              <p className='pd-panel__meta'>Control how each metric is calculated.</p>
            </div>
          </div>

          <div className='pd-settings-grid'>
            <div className='pd-setting-tile'>
              <label className='pd-toggle-row'>
                <input type='checkbox' checked={manual.gea} onChange={() => updateManual('gea')} />
                <span>Manual GEA</span>
              </label>
              <p className='pd-muted' style={{ margin: '0 0 4px' }}>GEA = GIA ×</p>
              <input
                name='gea_from_gia'
                type='number'
                step='0.001'
                value={ratios.gea_from_gia}
                onChange={onRatioChange}
                disabled={manual.gea}
                onWheel={preventNumberScroll}
                className='pd-setting-input'
              />
              <p className='pd-setting-note'>Default 1.053</p>
            </div>

            <div className='pd-setting-tile'>
              <label className='pd-toggle-row'>
                <input type='checkbox' checked={manual.gia} onChange={() => updateManual('gia')} />
                <span>Manual GIA</span>
              </label>
              <p className='pd-muted' style={{ margin: 0 }}>Enter GIA per floor.</p>
            </div>

            <div className='pd-setting-tile'>
              <label className='pd-toggle-row'>
                <input type='checkbox' checked={manual.nia} onChange={() => updateManual('nia')} />
                <span>Manual NIA</span>
              </label>
              <p className='pd-muted' style={{ margin: '0 0 4px' }}>NIA = GIA ×</p>
              <input
                name='nia_from_gia'
                type='number'
                step='0.001'
                value={ratios.nia_from_gia}
                onChange={onRatioChange}
                disabled={manual.nia}
                onWheel={preventNumberScroll}
                className='pd-setting-input'
              />
              <p className='pd-setting-note'>Default 0.8</p>
            </div>
          </div>
        </div>

        <div className='pd-panel'>
          <div className='pd-panel__head'>
            <div>
              <p className='pd-panel__title'>Areas by floor</p>
              <p className='pd-panel__meta'>Enter floor data or let Pebbledash derive it.</p>
            </div>
          </div>

          <div className='pd-table-wrapper'>
            <table className='pd-table pd-table--areas'>
              <thead>
                <tr>
                  <th>Entry</th>
                  <th>Label</th>
                  <th>GEA (m²)</th>
                  <th>GIA (m²)</th>
                  <th>NIA (m²)</th>
                  <th>Floor-to-floor (m)</th>
                </tr>
              </thead>
              <tbody>
                {floors.map((row, idx) => {
                  const computed = computeRow(row);
                  const isEntry = idx === entryIndex;
                  return (
                    <tr key={idx}>
                      <td>
                        <div className='pd-entry-radio'>
                          <input
                            type='radio'
                            name='entry'
                            checked={isEntry}
                            onChange={() => setEntryIndex(idx)}
                            aria-label={`Set ${labels[idx]} as entry floor`}
                          />
                        </div>
                      </td>
                      <td>{labels[idx]}</td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          inputMode='decimal'
                          value={manual.gea ? row.gea : computed.gea}
                          onChange={(e) => setCell(idx, 'gea', e.target.value)}
                          disabled={!manual.gea}
                          onWheel={preventNumberScroll}
                          onKeyDown={(event) => handleVerticalTab(event, idx, 'gea')}
                          ref={registerInput('gea', idx)}
                          className={`pd-table-input ${manual.gea ? 'pd-table-input--manual' : 'pd-table-input--auto'}`}
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          inputMode='decimal'
                          value={row.gia}
                          onChange={(e) => setCell(idx, 'gia', e.target.value)}
                          disabled={!manual.gia}
                          onWheel={preventNumberScroll}
                          onKeyDown={(event) => handleVerticalTab(event, idx, 'gia')}
                          ref={registerInput('gia', idx)}
                          className={`pd-table-input ${manual.gia ? 'pd-table-input--manual' : 'pd-table-input--auto'}`}
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          inputMode='decimal'
                          value={manual.nia ? row.nia : computed.nia}
                          onChange={(e) => setCell(idx, 'nia', e.target.value)}
                          disabled={!manual.nia}
                          onWheel={preventNumberScroll}
                          onKeyDown={(event) => handleVerticalTab(event, idx, 'nia')}
                          ref={registerInput('nia', idx)}
                          className={`pd-table-input ${manual.nia ? 'pd-table-input--manual' : 'pd-table-input--auto'}`}
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          inputMode='decimal'
                          value={row.h}
                          onChange={(e) => setCell(idx, 'h', e.target.value)}
                          placeholder='e.g. 3.9'
                          onWheel={preventNumberScroll}
                          onKeyDown={(event) => handleVerticalTab(event, idx, 'h')}
                          ref={registerInput('h', idx)}
                          className='pd-table-input pd-table-input--manual'
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className='pd-panel__footer'>
            <p className='pd-panel__footer-note'>
              Tip: Use the entry column to set the arrival level. Rows below the selected floor are labelled First, Second, Third…
              and rows above become Basement levels.
            </p>
            <div className='pd-panel__actions'>
              <button className='pd-btn pd-btn--ghost' type='button' onClick={() => navigate(`/projects/${id}`)}>
                Cancel
              </button>
              <button className='pd-btn pd-btn--primary' type='button' onClick={onSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
